import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

function PartnerFlowsTab({ partner, filter, onClearFilter }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (partner) {
      fetchFlows();
    }
  }, [partner, filter]);

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const response = await api.getPartnerFlows(partner._id);
      let allFlows = response.data.flows || [];
      
      // Apply filter if present
      if (filter && filter.type === 'config') {
        console.log('Filtering flows by config ID:', filter.id);
        console.log('All flows before filter:', allFlows.map(f => ({ 
          id: f._id, 
          flowConfigId: f.flowConfigId, 
          flowConfigIdType: typeof f.flowConfigId 
        })));
        
        allFlows = allFlows.filter(flow => {
          // Skip flows with null/undefined flowConfigId
          if (!flow.flowConfigId) {
            console.log(`Flow ${flow._id}: flowConfigId is null/undefined, skipping`);
            return false;
          }
          
          // Handle both string and ObjectId comparison
          const flowConfigId = typeof flow.flowConfigId === 'object' && flow.flowConfigId._id 
            ? flow.flowConfigId._id 
            : flow.flowConfigId;
          const matches = flowConfigId?.toString() === filter.id?.toString();
          console.log(`Flow ${flow._id}: flowConfigId=${flowConfigId}, filterId=${filter.id}, matches=${matches}`);
          return matches;
        });
        
        console.log('Filtered flows:', allFlows.length);
      }
      
      setFlows(allFlows);
    } catch (err) {
      console.error('Error fetching flows:', err);
      setMessage({ type: 'error', text: 'Failed to load flows' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (flow) => {
    try {
      const response = await api.getFlowDetail(flow._id);
      setSelectedFlow(response.data.flow);
      setShowDetails(true);
    } catch (err) {
      console.error('Error fetching flow details:', err);
      setMessage({ type: 'error', text: 'Failed to load flow details' });
    }
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedFlow(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return { bg: '#dcfce7', color: '#166534' };
      case 'completed':
        return { bg: '#dbeafe', color: '#1e40af' };
      case 'expired':
        return { bg: '#fee2e2', color: '#dc2626' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const calculateProgress = (flow) => {
    if (!flow.steps || flow.steps.length === 0) return 0;
    const completedSteps = flow.steps.filter(s => s.completed).length;
    return Math.round((completedSteps / flow.steps.length) * 100);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
        Loading flows...
      </div>
    );
  }

  if (showDetails && selectedFlow) {
    const progress = calculateProgress(selectedFlow);
    const statusStyle = getStatusColor(selectedFlow.status);

    return (
      <div>
        <button
          onClick={handleCloseDetails}
          style={{
            padding: '8px 16px',
            marginBottom: '20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151'
          }}
        >
          ← Back to Flows List
        </button>

        <div style={{
          padding: '20px',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111827' }}>
                {selectedFlow.config?.name || 'Unnamed Flow'}
              </h3>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                {selectedFlow.config?.description}
              </p>
            </div>
            <span style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '100px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: statusStyle.bg,
              color: statusStyle.color
            }}>
              {selectedFlow.status}
            </span>
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Progress
              </span>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                {progress}%
              </span>
            </div>
            <div style={{
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '100px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: '#4f46e5',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Flow Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            fontSize: '14px'
          }}>
            <div>
              <span style={{ color: '#6b7280' }}>User ID:</span>{' '}
              <span style={{ color: '#111827', fontFamily: 'monospace' }}>
                {selectedFlow.userId?.substring(0, 12)}...
              </span>
            </div>
            <div>
              <span style={{ color: '#6b7280' }}>Created:</span>{' '}
              <span style={{ color: '#111827' }}>
                {new Date(selectedFlow.createdAt).toLocaleDateString()}
              </span>
            </div>
            {selectedFlow.completedAt && (
              <div>
                <span style={{ color: '#6b7280' }}>Completed:</span>{' '}
                <span style={{ color: '#111827' }}>
                  {new Date(selectedFlow.completedAt).toLocaleDateString()}
                </span>
              </div>
            )}
            {selectedFlow.expiresAt && (
              <div>
                <span style={{ color: '#6b7280' }}>Expires:</span>{' '}
                <span style={{ color: '#111827' }}>
                  {new Date(selectedFlow.expiresAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Steps */}
        <div>
          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
            Steps
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedFlow.steps?.map((step, index) => (
              <div
                key={index}
                style={{
                  padding: '15px',
                  backgroundColor: step.completed ? '#f0fdf4' : '#ffffff',
                  border: `1px solid ${step.completed ? '#86efac' : '#e5e7eb'}`,
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: step.completed ? '#22c55e' : '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {step.completed ? '✓' : index + 1}
                      </div>
                      <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                        {selectedFlow.config?.steps?.[index]?.goal || `Step ${index + 1}`}
                      </h5>
                    </div>
                    <p style={{ margin: '0 0 8px 0', paddingLeft: '34px', fontSize: '13px', color: '#6b7280' }}>
                      {selectedFlow.config?.steps?.[index]?.description}
                    </p>
                    <div style={{ paddingLeft: '34px', fontSize: '12px', color: '#9ca3af' }}>
                      {step.completed ? (
                        <span>
                          Completed: {new Date(step.completedAt).toLocaleString()}
                        </span>
                      ) : (
                        <span>
                          Progress: {step.progress || 0} / {selectedFlow.config?.steps?.[index]?.criteria?.value || 1}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px', fontSize: '20px' }}>
        Active Flows
      </h2>

      {filter && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#1e40af' }}>
            <strong>Filtered by:</strong> Flow Config "{filter.name}"
          </div>
          <button
            onClick={onClearFilter}
            style={{
              padding: '4px 12px',
              backgroundColor: 'white',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: 'pointer',
              color: '#1e40af'
            }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          {message.text}
        </div>
      )}

      {flows.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p>No active flows found</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '15px'
        }}>
          {flows.map((flow) => {
            const progress = calculateProgress(flow);
            const statusStyle = getStatusColor(flow.status);

            return (
              <div
                key={flow._id}
                style={{
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => handleViewDetails(flow)}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '6px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#111827' }}>
                      {flow.config?.name || 'Unnamed Flow'}
                    </h4>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '100px',
                      fontSize: '11px',
                      fontWeight: '500',
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color
                    }}>
                      {flow.status}
                    </span>
                  </div>
                  <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                    {flow.userId?.substring(0, 12)}...
                  </p>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    height: '6px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '100px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: '#4f46e5'
                    }} />
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    {progress}% Complete
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  Created: {new Date(flow.createdAt).toLocaleDateString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PartnerFlowsTab;
